import { useState } from "react";
import {
  Table,
  Dropdown,
  Menu,
  Button,
  Avatar,
  Image,
  message,
  Modal,
} from "antd";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import {
  getMenuItems,
  createMenuItem,
  updateMenuItem,
} from "../actions/merchant_actions";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import MenuItemFomItemsTable from "./MenuItemFomItemsTable";
import { useTranslation } from "react-i18next";

export default function MenuItems() {
  const { t } = useTranslation();
  const accUrn = useSelector((state) => state.account.urn);

  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  const {
    isLoading,
    data: itemsData,
    refetch,
  } = useQuery({
    queryKey: ["menuItems", accUrn],
    queryFn: () => getMenuItems({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  const openAddModal = () => {
    setSelectedMenuItem(null);
    setItemModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedMenuItem) {
        await updateMenuItem({
          account_urn: accUrn,
          itemUrn: selectedMenuItem.urn,
          data: values,
        });
        message.success(t("menu_item_updated"));
      } else {
        await createMenuItem({ account_urn: accUrn, data: values });
        message.success(t("menu_item_created"));
      }

      setItemModalVisible(false);
      setSelectedMenuItem(null);
      refetch();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("something_went_wrong");
      message.error(msg);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: t("delete_item_question"),
      content: t("delete_item_warning"),
      okText: t("yes_delete"),
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          // await deleteMenuItem({ account_urn: accUrn, urn: item.urn });
          message.success(t("menu_item_deleted"));
          refetch();
        } catch {
          message.error(t("failed_to_delete"));
        }
      },
    });
  };

  const columns = [
    {
      title: t("image"),
      key: "image",
      width: 70,
      render: (_, record) => {
        const name = record?.data?.name || "";
        const image = record?.data?.imagePath;

        return (
          <Image
            src={image || "/assets/images/bg/placeholder.jpg"}
            width={80}
            height={80}
            style={{ objectFit: "cover", borderRadius: 4 }}
            fallback="/assets/images/bg/placeholder.jpg"
          />
        );
      },
    },
    {
      title: t("sku"),
      dataIndex: ["data", "sku"],
      key: "sku",
    },
    {
      title: t("name"),
      dataIndex: ["data", "name"],
      key: "name",
    },
    {
      title: t("title"),
      dataIndex: ["data", "title"],
      key: "title",
    },
    {
      title: t("type"),
      dataIndex: ["data", "type"],
      key: "type",
    },
    {
      title: t("usage"),
      dataIndex: ["data", "usage"],
      key: "usage",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="edit"
              onClick={() => {
                setSelectedMenuItem(record);
                setItemModalVisible(true);
              }}
            >
              {t("edit")}
            </Menu.Item>
            <Menu.Item key="delete" danger onClick={() => handleDelete(record)}>
              {t("delete")}
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu}>
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Table
        loading={isLoading}
        dataSource={itemsData}
        columns={columns}
        rowKey="urn"
        pagination={false}
        size="middle"
      />

      {/* Shared Modal for Add + Edit */}
      <MenuItemFomItemsTable
        visible={itemModalVisible}
        onClose={() => {
          setItemModalVisible(false);
          setSelectedMenuItem(null);
        }}
        onSubmit={handleSubmit}
        initialValues={selectedMenuItem}
      />
    </>
  );
}
